/**
 * HOOK: the idempotency key a write sends as `operationId`. Retrying the same payload after a failure (or an
 * unknown outcome) reuses its key, so the server applies and announces it once; a success, or a different
 * payload, gets a fresh key. See "A retried send reuses its request key" in REACT_CONVENTIONS.md.
 */
import _ from 'lodash';
import { useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface IPendingKey {
  payload: unknown;
  key: string;
}

export const useRetryKey = () => {
  const pending = useRef<IPendingKey[]>([]);

  return useCallback(async <T>(payload: unknown, send: (operationId: string) => Promise<T>): Promise<T> => {
    let entry = pending.current.find((candidate) => _.isEqual(candidate.payload, payload));
    if (!entry) {
      entry = { payload: _.cloneDeep(payload), key: uuidv4() };
      pending.current = [...pending.current, entry];
    }

    const result = await send(entry.key);
    pending.current = pending.current.filter((candidate) => candidate !== entry);
    return result;
  }, []);
};
